<template>
  <PcWrapper v-if="!isMobile" />
  <MobileWrapper v-else />
  <onlyCsr>
    <GDPRPopup :isoCode="isoCode" />
    <FAQIcon v-if="faqIconVisibility && state.showFAQ" />
    <FeedbackIcon v-if="feedbackVisibility" />
    <EuUserDialog :visible="state.euUserVisible" @close="state.euUserVisible = false" />
    <InformationGatheringPCDialog />
  </onlyCsr>
</template>

<script lang="ts">
import { getCurrentInstance, computed, defineComponent, onMounted, reactive } from 'vue'
import createI18n from '@/i18n'
import { useStoreHooks } from '@/store/hook'
import PcWrapper from '../pageWrapper/pcWrapper.vue'
import MobileWrapper from '../pageWrapper/mobileWrapper.vue'
import { useRoute, useRouter } from 'vue-router'
import GDPRPopup from '@/components/gdprPopup/index.vue'
import EuUserDialog from '@/components/dialogs/EuUserDialog.vue'
import FAQIcon from '@/components/FAQIcon/index.vue'
import FeedbackIcon from '@/components/FeedbackIcon/index.vue'
import InformationGatheringPCDialog from '@/components/dialogs/InformationGatheringPCDialog.vue'
import { onlyCsr } from 'ssr-hoc-vue3'
import { initVueDirective } from '@/utils/directive'
import { useClientCompatibleHook } from '@/utils/hooks'
import { AtModel } from '@/utils/atModel'
import { UrlUtil } from '@/utils'
import { StoreUtils } from '@/store/StoreUtil'
import { loginBySign, getFAQConfigList } from '@/request/api/common'
import { updateUserInfo } from '@/request/api/user'
import * as Sentry from '@sentry/vue'
import { Integrations } from '@sentry/tracing'

// 在这里可以进行一些全局组件的注册逻辑
export default defineComponent({
  components: {
    PcWrapper,
    MobileWrapper,
    GDPRPopup,
    FAQIcon,
    onlyCsr,
    FeedbackIcon,
    EuUserDialog,
    InformationGatheringPCDialog
  },
  setup () {
    const store = useStoreHooks()
    const { state: _state, getters, commit } = store
    const $route = useRoute()
    const router = useRouter()
    const state = reactive({
      euUserVisible: false,
      showFAQ: false
    })
    const isLogin = getters['global/isLogin']
    const currentLangKey = computed(() => _state.global.currentLangKey)
    const isoCode = computed(() => _state.global.isoCode)
    // 在这里存一个全局使用的store
    StoreUtils.store = store
    const seoConfigList = _state.global.seoConfig
    const tenantId = computed(() => _state.global.tenantId)
    const appLoading = computed(() => _state.global.appLoading)
    // const showPcPage = computed(() => $route.path.split('/')[1] !== 'mobile')
    const currentPage = computed(() => $route.path)
    const { isMobile } = useClientCompatibleHook()
    const faqIconVisibility = computed(() => {
      const FAQ_DISPLAY_PAGES = ['/', '/my-learning', '/general-security-certification/overview', '/hikvision-security-certification/overview']
      return FAQ_DISPLAY_PAGES.some(i => {
        const path = isMobile.value ? `/mobile${i}` : i
        return path === currentPage.value
      })
    })
    const feedbackVisibility = computed(() => {
      const FEEDBACK_HIDE_PAGES = ['/exam', '/practical-exam']
      return FEEDBACK_HIDE_PAGES.some(i => {
        const path = isMobile.value ? `/mobile${i}` : i
        return path !== currentPage.value
      })
    })
    /**
     * @description 根据页面应用对应的seo配置
     * */
    const applySeoConfig = (path: string) => {
      const seoItem = seoConfigList?.find(i => i.url === path || `${i.url}/` === path)
      let title = seoItem?.title || _state.global.defaultPageTitle
      let description = seoItem?.description || ''
      if (path.includes('/certification/detail')) {
        // 认证详情页面
        title = _state.certification.detail.certificationDetail.certificationName
        description = _state.certification.detail.certificationDetail.description
      }
      if (path.includes('/course/detail')) {
        // 课程页面
        title = _state.course.detail.courseDetail.courseName
        description = _state.course.detail.courseDetail.description
      }
      const descriptionMeta = document.querySelector('meta[name=description]')
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', description)
      }
      document.title = title
    }
    /**
     * @description hpp端自动免登录
     * */
    const loginSSOBySign = () => {
      // 不要从route.query里面拿参数，部分ios手机有兼容性问题
      const urlParams = UrlUtil.getUrlParams(window.location.href)
      // console.log('loginSSOBySign', urlParams)
      const { sign, appid } = urlParams as { sign: string, appid: string } || {}
      if (!sign) {
        commit('common/setHppLoginCheckStatus', '1')
        return
      }
      const oneHikIdSign = decodeURIComponent(sign)
      // loginBySign会塞进来cookie相关信息,
      loginBySign({ oneHikIdSign, appId: appid }).then(res => {
        // 重新组装一下url，否则刷新后还有sign，就会重新调用verify方法
        if (!(res.data as { verifySignErrorMsg?: string })?.verifySignErrorMsg) {
          window.location.href = `${window.location.origin}${window.location.pathname}`
        } else {
          console.error('ON_HPP_LOGIN_FAIL', (res.data as { verifySignErrorMsg?: string })?.verifySignErrorMsg)
          commit('common/setHppLoginCheckStatus', '1')
        }
      })
    }

    // 当个人偏好和用户中心偏好不同时，同步个人偏好到个人中心
    const syncLanguageWithUserInfo = () => {
      if (!_state.global.userInfo?.languageId || _state.global.userInfo?.languageId !== currentLangKey.value) {
        const userInfo = {
          ..._state.global.userInfo,
          languageId: currentLangKey.value as number
        }
        updateUserInfo(userInfo).then(res => {
          if (res.code === 200) {
            commit('global/setUserInfo', userInfo)
          }
        })
      }
    }

    const getFAQConfig = async () => {
      const res: any = await getFAQConfigList()
      if (res.code === 200 && res.data.length > 0) {
        state.showFAQ = true
      }
    }

    onMounted(() => {
      // 在layout/index.ts里处理过一次，这里再处理一次的目的是，防止某些页面是走csr模式渲染的
      AtModel.doPageEvent(_state.global.userInfo)
      applySeoConfig(location.pathname)
      // Hpp平台免登录
      loginSSOBySign()
      getFAQConfig()
      if ((_state.global.isEU || _state.global.isAsia) && getters['global/isLogin'] && _state.global.userInfo?.migrationPopStatus === '1') {
        state.euUserVisible = true
      }
      if (isLogin) {
        syncLanguageWithUserInfo()
      }
    })

    // router 钩子守卫
    router.beforeResolve((to) => {
      const path = `/${window.prefix}${to.path}`
      applySeoConfig(path)
      window.scrollTo(0, 0)
    })
    router.afterEach(() => {
      AtModel.doPageEvent(_state.global.userInfo)
    })
    const initI18n = () => {
      const app = getCurrentInstance()?.appContext.app
      if (app) {
        app.use(createI18n(_state.global.messages || {}, tenantId.value))
        initVueDirective(app)
      }
    }
    const initSentry = () => {
      const app = getCurrentInstance()?.appContext.app
      if (app && Sentry) {
        Sentry.init({
          app,
          dsn: 'https://70d555ec4f2c49938cb538179c77bffd@datacollect.hikvision.com/428',
          integrations: [
            new Integrations.BrowserTracing({
              routingInstrumentation: Sentry.vueRouterInstrumentation(router),
              tracingOrigins: [
                'elearning.hikvision.com'
              ]
            })
          ],
          tracesSampleRate: 1.0
        })
      }
    }
    initSentry()
    initI18n()
    return {
      isMobile,
      currentPage,
      faqIconVisibility,
      feedbackVisibility,
      state,
      appLoading,
      isoCode
    }
  }
})
</script>
